<template>
  <ClientOnly>
    <template #default>
      <div
        ref="reference"
        @mouseenter="showTooltip"
        @mouseleave="closeTooltip"
        @focus="showTooltip"
        @blur="closeTooltip"
      >
        <slot />
      </div>
      <Teleport to="#teleports">
        <div
          ref="tooltipWrapperRef"
          class="tooltip"
          :class="{ 'tooltip--visible': isVisible }"
        >
          <Transition name="fade">
            <div v-if="isVisible">
              <slot name="content" />
            </div>
          </Transition>
        </div>
      </Teleport>
    </template>

    <template #fallback>
      <slot />
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import {
  createPopper,
  type Instance,
  type OptionsGeneric
} from '@popperjs/core'

const props = withDefaults(
  defineProps<{ offsetX?: number; offsetY?: number }>(),
  { offsetX: 0, offsetY: 0 }
)

const modifiers = [
  {
    name: 'offset',
    options: {
      offset: [props.offsetX, props.offsetY]
    }
  }
]

type Modifiers = (typeof modifiers)[number]

const options: Partial<OptionsGeneric<Modifiers>> = {
  modifiers
}

const isVisible = ref(false)

const popper = ref<Instance | null>(null)
const reference = ref<HTMLDivElement | null>(null)
const tooltipWrapperRef = ref<HTMLDivElement | null>(null)

const showTooltip = () => {
  isVisible.value = true

  // Enable event listeners
  popper.value?.setOptions(prevOptions => ({
    ...prevOptions,
    modifiers: [...modifiers, { name: 'eventListeners', enabled: true }]
  }))

  popper.value?.update()
}
const closeTooltip = () => {
  isVisible.value = false

  // Disable event listeners
  popper.value?.setOptions(prevOptions => ({
    ...prevOptions,
    modifiers: [...modifiers, { name: 'eventListeners', enabled: false }]
  }))
}

onMounted(async () => {
  await nextTick()

  if (!reference.value || !tooltipWrapperRef.value) return

  popper.value = createPopper(reference.value, tooltipWrapperRef.value, options)
})

onBeforeUnmount(() => {
  popper.value?.destroy()
})
</script>

<style scoped lang="scss">
.tooltip {
  z-index: 1;
  pointer-events: none;

  &--visible {
    pointer-events: auto;
  }
}
</style>
